import { App as CapApp, URLOpenListenerEvent } from '@capacitor/app';
import { Browser } from '@capacitor/browser';
import { LiveUpdate } from '@capawesome/capacitor-live-update';
import { datadogRum } from '@datadog/browser-rum';
import { runUrlInterceptors } from '../../capacitor-plugins/appUrl-interceptors';
import { trackEvent, useNavigation } from '@helios/frontend-core';
import { useEffect, useRef } from 'react';
import { useUser } from '../../api/user/useUser';
import { ExternalLinkEvent, onExternalLink } from '../../capacitor-plugins/webview-recovery';
import { hideIntercomMessenger } from '../../components/IntercomBubble/IntercomBubble';
import { refreshSessionTimeOfDay } from '../../components/RiveHome/sessionTimeOfDay';
import { PlatformConfig } from '../../config';
import { requestInitialAchievements } from '../../utils/achievementPolling';
import { isLiveUpdateEnabled, PREVENT_LIVE_UPDATE_RELOAD_KEY } from '../../utils/initialLiveUpdateSync';

export { PREVENT_LIVE_UPDATE_RELOAD_KEY } from '../../utils/initialLiveUpdateSync';

const MARKETING_HOSTS = ['palmetto.com', 'www.palmetto.com'];
const MARKETING_LEARN_SECTIONS = ['home-electrification', 'solar', 'climate', 'policy'];

const normalizePath = (path: string): string => (path.startsWith('/') ? path : `/${path}`);

export const resolveInAppPath = (event: ExternalLinkEvent): string | null => {
  const path = normalizePath(event.path);

  if (event.host === PlatformConfig.hostname) {
    return `${path}${event.search}`;
  }

  const [section] = path.split('/').filter(Boolean);
  if (MARKETING_HOSTS.includes(event.host) && MARKETING_LEARN_SECTIONS.includes(section)) {
    return `/learn${path}${event.search}`;
  }

  return null;
};

export const checkForAppUpdate = async (): Promise<void> => {
  if (!isLiveUpdateEnabled() || localStorage[PREVENT_LIVE_UPDATE_RELOAD_KEY] === 'true') return;
  try {
    if (localStorage.shouldReloadApp === 'true') {
      await LiveUpdate.reload();
    } else {
      const { nextBundleId } = await LiveUpdate.sync();
      localStorage.shouldReloadApp = nextBundleId !== null;
    }
  } catch (error) {
    datadogRum.addError('Live update check failed', { error });
  }
};

const useNativeAppListener = (): void => {
  const { openURL } = useNavigation();
  const openURLRef = useRef(openURL);
  openURLRef.current = openURL;
  const { user, isAuthenticated } = useUser();

  useEffect(() => {
    const listenerPromise = CapApp.addListener('appUrlOpen', async (data: URLOpenListenerEvent) => {
      if (await runUrlInterceptors(data.url)) return;
      const { pathname, search } = new URL(data.url);
      if (pathname) {
        openURLRef.current(`${pathname}${search}`);
      }
    });
    return () => {
      listenerPromise.then((listener) => listener.remove());
    };
  }, []);

  useEffect(() => {
    return onExternalLink((event: ExternalLinkEvent) => {
      hideIntercomMessenger();
      const inAppPath = resolveInAppPath(event);
      if (inAppPath) {
        openURLRef.current(inAppPath);
      } else {
        Browser.open({ url: event.url });
      }
    });
  }, []);

  useEffect(() => {
    const handleResume = async () => {
      refreshSessionTimeOfDay();
      if (isAuthenticated && user?._id) {
        trackEvent('Application Opened');
        requestInitialAchievements();
      }
      await checkForAppUpdate();
    };
    const resumeListener = CapApp.addListener('resume', handleResume);

    return () => {
      resumeListener.then((listener) => listener.remove());
    };
  }, [isAuthenticated, user?._id]);

  useEffect(() => {
    const handlePause = async () => {
      await checkForAppUpdate();
    };
    const pauseListener = CapApp.addListener('pause', handlePause);

    return () => {
      pauseListener.then((listener) => listener.remove());
    };
  }, [isAuthenticated, user?._id]);
};

export default useNativeAppListener;
